import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { useSessionQuery, useSignInMutation, useSignOutMutation } from "../lib/authQueries";
import { useTRPC } from "../lib/trpc";

/** Mirrors the server's bound (PLATFORM_ADMIN_PASSWORD_MIN_LENGTH) so the form refuses early. */
const PASSWORD_MIN_LENGTH = 8;

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-lg font-bold tracking-tight">
            Loot <span className="text-primary">OS</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">{children}</CardContent>
      </Card>
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      {children}
    </div>
  );
}

/**
 * The landing page for a platform-admin invite link (/accept-admin-invite?id=…), outside
 * the app shell: the invitee has no account yet. Flow: preview the invitation (public by
 * unguessable id) → choose and confirm a password → accept creates the account, already a
 * platform admin → sign in with that password → the tenant list. An invitee whose account
 * appeared meanwhile (through a tenant invitation) is upgraded instead and sent to sign in.
 */
export function AcceptAdminInvitePage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { id } = useSearch({ strict: false }) as { id?: string };
  const invite = useQuery(trpc.platformInvites.get.queryOptions(id ? { id } : { id: "" }));
  const { data: session, isPending: sessionPending } = useSessionQuery();
  const accept = useMutation(trpc.platformInvites.accept.mutationOptions());
  const signIn = useSignInMutation();
  const signOut = useSignOutMutation();

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [accountExisted, setAccountExisted] = useState(false);
  const [accepted, setAccepted] = useState(false);

  if (!id) {
    return (
      <Shell>
        <ErrorBox>This invite link is missing its id. Ask for a fresh link.</ErrorBox>
      </Shell>
    );
  }
  if (invite.isLoading || sessionPending) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">Loading invitation…</p>
      </Shell>
    );
  }
  if (!invite.data) {
    return (
      <Shell>
        <ErrorBox>This invitation doesn't exist. Ask for a fresh link.</ErrorBox>
        <Link to="/login" className={buttonVariants({ variant: "outline" })}>
          Go to sign in
        </Link>
      </Shell>
    );
  }

  const { email, inviterName, status, expired } = invite.data;

  // The account already had a password of its own, which accept never replaces.
  if (accountExisted) {
    return (
      <Shell>
        <p className="text-sm">
          <span className="font-mono">{email}</span> already had a Loot OS account, so it now has
          platform access. Sign in with its existing password.
        </p>
        <Link to="/login" className={buttonVariants()}>
          Go to sign in
        </Link>
      </Shell>
    );
  }

  // Accepted here, but the automatic sign-in failed: the link is consumed, so offering the
  // form again would only hit a used invitation. The account exists; they sign in themselves.
  if (accepted) {
    return (
      <Shell>
        <p className="text-sm">
          Your account is ready. Sign in as <span className="font-mono">{email}</span> with the
          password you chose.
        </p>
        <Link to="/login" className={buttonVariants()}>
          Go to sign in
        </Link>
      </Shell>
    );
  }

  if (status === "accepted") {
    return (
      <Shell>
        <p className="text-sm">This invitation has already been accepted.</p>
        <Link to="/login" className={buttonVariants()}>
          Go to sign in
        </Link>
      </Shell>
    );
  }
  if (status !== "pending" || expired) {
    return (
      <Shell>
        <ErrorBox>
          This invitation has {expired ? "expired" : "been canceled"}. Ask a platform admin to send
          a new one.
        </ErrorBox>
        <Link to="/login" className={buttonVariants({ variant: "outline" })}>
          Go to sign in
        </Link>
      </Shell>
    );
  }

  // Signed in as someone: the invitation is for a brand-new account, not this one.
  if (session) {
    return (
      <Shell>
        <p className="text-sm">
          This invitation is for <span className="font-mono">{email}</span>, but you're signed in as{" "}
          <span className="font-mono">{session.user.email}</span>.
        </p>
        <Button
          variant="outline"
          onClick={() => {
            // The mutation seeds the signed-out session, flipping this page to the form.
            signOut.mutate();
          }}
        >
          Sign out and start over
        </Button>
      </Shell>
    );
  }

  const mismatch = confirm.length > 0 && confirm !== password;
  const tooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const canSubmit =
    !busy && password.length >= PASSWORD_MIN_LENGTH && confirm === password && !mismatch;

  // Failures are toasted by the query client; the page only re-enables its button.
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const result = await accept.mutateAsync({ id, password, name: name.trim() || undefined });
      if (result.accountExisted) {
        setAccountExisted(true);
        return;
      }
      // From here the invitation is consumed; a sign-in failure lands on the recovery state above.
      setAccepted(true);
      // Resolves only once the session is in cache, so the redirect can't bounce off the guard.
      await signIn.mutateAsync({ email, password });
      void navigate({ to: "/", replace: true });
    } catch {
      // Toasted by the query client; re-enable the form (or, once accepted, the recovery state renders).
      setBusy(false);
    }
  };

  return (
    <Shell>
      <CardDescription>
        <strong>{inviterName}</strong> invited you to be a platform admin on Loot OS. Choose a
        password to create your account.
      </CardDescription>
      <form onSubmit={submit} className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="admin-invite-email">Email</Label>
          {/* The invitation names the email; the account is created under exactly this one. */}
          <Input
            id="admin-invite-email"
            type="email"
            value={email}
            readOnly
            className="bg-muted/50"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="admin-invite-name">Name</Label>
          <Input
            id="admin-invite-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            autoComplete="name"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="admin-invite-password">Password</Label>
          <Input
            id="admin-invite-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={PASSWORD_MIN_LENGTH}
            autoComplete="new-password"
            required
          />
          {tooShort && (
            <p className="text-xs text-muted-foreground">
              At least {PASSWORD_MIN_LENGTH} characters.
            </p>
          )}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="admin-invite-confirm">Confirm password</Label>
          <Input
            id="admin-invite-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            minLength={PASSWORD_MIN_LENGTH}
            autoComplete="new-password"
            aria-invalid={mismatch || undefined}
            required
          />
          {mismatch && <p className="text-xs text-destructive">The passwords don't match.</p>}
        </div>
        <Button type="submit" className="w-full" disabled={!canSubmit}>
          {busy ? "Creating account…" : "Create account"}
        </Button>
      </form>
    </Shell>
  );
}
